import { Button, ButtonProps } from '@/components/ui/button';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import {
    Drawer,
    DrawerClose,
    DrawerContent,
    DrawerDescription,
    DrawerFooter,
    DrawerHeader,
    DrawerTitle,
    DrawerTrigger,
} from '@/components/ui/drawer';
import useMediaQuery from '@/hooks/use-media-query';
import { cn } from '@/lib/utils';
import { screenSize } from '@/styles/styles';
import * as React from 'react';
import CustomButton from '../custom-button';
import { ButtonCustomProps } from '../custom-button/button';
import { ScrollArea } from '../ui/scroll-area';

export enum CustomDialogType {
    Dialog,
    Drawer,
    Reponsive,
}

interface CustomDialogCancleProps
    extends ButtonCustomProps, React.RefAttributes<HTMLButtonElement> {
    children?: React.ReactNode;
}

const DialogSize = {
    sm: 'lg:w-[calc(100vw-24px)] lg:max-w-sm',
    lg: 'lg:w-[calc(100vw-24px)] lg:max-w-lg',
    xl: 'lg:w-[calc(100vw-24px)] lg:max-w-xl',
    '2xl': 'lg:w-[calc(100vw-24px)] lg:max-w-2xl',
    '3xl': 'lg:w-[calc(100vw-24px)] lg:max-w-3xl',
    '4xl': 'lg:w-[calc(100vw-24px)] lg:max-w-4xl',
    '5xl': 'lg:w-[calc(100vw-24px)] lg:max-w-5xl',
    '6xl': 'lg:w-[calc(100vw-24px)] lg:max-w-6xl',
    '7xl': 'lg:w-[calc(100vw-24px)] lg:max-w-7xl',
};

type ModalSize = keyof typeof DialogSize;

export interface CustomDialogProps {
    headerTitle: React.ReactNode;
    headerDescription?: React.ReactNode;
    footer?: React.ReactNode;
    trigger?: React.ReactNode;
    cancel?: CustomDialogCancleProps;
    classNameContent?: string;
    classNameHeader?: string;
    classNameFooter?: string;
    /**Scrollarea này bị lỗi khi sử dụng với một số component custom ==> Tắt nó đi để tránh lỗi
     * @example CustomSelectMultiple
     */
    enableScrollArea?: boolean;
    classNameScrollArea?: string;
    mediaQueryBreakdown?: number;
    modalSize?: ModalSize;
    dialogProps?: (
        | React.ComponentProps<typeof Dialog>
        | React.ComponentProps<typeof Drawer>
    ) & { footerAside?: boolean };
    dialogContentProps?:
        | React.ComponentProps<typeof DialogContent>
        | React.ComponentProps<typeof DrawerContent>;
    type?: CustomDialogType;
}

const renderCancelBtn = (cancel: CustomDialogCancleProps) => {
    const { children, ...props } = cancel;

    return (
        <CustomButton.Root variant='outline' {...props}>
            {cancel.children}
        </CustomButton.Root>
    );
};

export function CustomDialog({
    children,
    trigger,
    headerTitle,
    headerDescription,
    footer,
    classNameContent,
    classNameHeader,
    classNameFooter,
    enableScrollArea = true,
    classNameScrollArea,
    cancel,
    modalSize = 'lg',
    mediaQueryBreakdown = screenSize.lg,
    dialogProps,
    dialogContentProps,
    type,
}: React.PropsWithChildren<CustomDialogProps>) {
    const [open, setOpen] = React.useState(false);
    const isDesktop = useMediaQuery(`(min-width: ${mediaQueryBreakdown}px)`);

    /**
     * Chưa detect xong màn hình thì chưa hiện
     */
    if (typeof isDesktop !== 'boolean') {
        return <React.Fragment />;
    }

    if (
        type != CustomDialogType.Drawer &&
        (type == CustomDialogType.Dialog || isDesktop)
    ) {
        const _content = (
            <>
                <DialogHeader
                    className={cn(
                        'sticky top-0 z-10 min-h-8 bg-background',
                        classNameHeader
                    )}
                >
                    <DialogTitle className='text-lg leading-7'>
                        {headerTitle}
                    </DialogTitle>
                    {headerDescription && (
                        <DialogDescription>
                            {headerDescription}
                        </DialogDescription>
                    )}
                </DialogHeader>
                <div className='wrapper-child px-1 pb-10 pt-3'>{children}</div>
                <DialogFooter
                    className={cn(
                        'bg-background',
                        dialogProps?.footerAside
                            ? 'fixed bottom-0 left-[102%] z-10 grid items-start justify-start space-y-2 break-words rounded-md border px-3 pb-2 pt-10 shadow-lg'
                            : 'fixed bottom-0.5 right-0 z-10 !space-x-2 pb-5 pr-5',
                        classNameFooter
                    )}
                >
                    {footer}
                    {!cancel ? null : (
                        <DialogClose className='!ml-0 h-auto' asChild>
                            {renderCancelBtn(cancel)}
                        </DialogClose>
                    )}
                </DialogFooter>
            </>
        );

        return (
            <Dialog open={open} onOpenChange={setOpen} {...dialogProps}>
                <DialogTrigger asChild>{trigger}</DialogTrigger>
                <DialogContent
                    tabIndex={undefined}
                    className={cn(
                        'gap-0 pr-2',
                        `${DialogSize[modalSize]} `,
                        classNameContent
                    )}
                    {...(dialogContentProps ?? {})}
                    aria-describedby={'dialog-description'}
                >
                    {enableScrollArea ? (
                        <ScrollArea
                            className={cn(
                                'max-h-[90vh] pr-3',
                                classNameScrollArea
                            )}
                        >
                            {_content}
                        </ScrollArea>
                    ) : (
                        _content
                    )}
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Drawer open={open} onOpenChange={setOpen} {...dialogProps}>
            <DrawerTrigger asChild>{trigger}</DrawerTrigger>
            <DrawerContent
                className={cn('mx-2 grid border-none', classNameContent)}
                onOpenAutoFocus={(e) => {
                    e.preventDefault();
                }}
                {...(dialogContentProps ?? {})}
            >
                {enableScrollArea ? (
                    <ScrollArea
                        className={cn(
                            'max-h-[75vh] [&>div>div]:!block',
                            classNameScrollArea
                        )}
                    >
                        <DrawerHeader
                            className={cn(
                                'sticky top-0 z-10 bg-background pb-1',
                                classNameHeader
                            )}
                        >
                            <DrawerTitle>{headerTitle}</DrawerTitle>
                            <DrawerDescription>
                                {headerDescription}
                            </DrawerDescription>
                        </DrawerHeader>
                        <div className='wrapper-child pl-3 pr-2'>
                            {children}
                        </div>
                        <DrawerFooter
                            className={cn(
                                'sticky bottom-0 z-10 bg-background pb-2',
                                classNameFooter
                            )}
                        >
                            {footer}
                            {!cancel ? null : (
                                <DrawerClose asChild>
                                    {renderCancelBtn(cancel)}
                                </DrawerClose>
                            )}
                        </DrawerFooter>
                    </ScrollArea>
                ) : (
                    <>
                        <DrawerHeader
                            className={cn(
                                'sticky top-0 z-10 bg-background pb-1',
                                classNameHeader
                            )}
                        >
                            <DrawerTitle>{headerTitle}</DrawerTitle>
                            <DrawerDescription>
                                {headerDescription}
                            </DrawerDescription>
                        </DrawerHeader>
                        <div className='wrapper-child pl-3 pr-2'>
                            {children}
                        </div>
                        <DrawerFooter
                            className={cn(
                                'sticky bottom-0 z-10 bg-background pb-2',
                                classNameFooter
                            )}
                        >
                            {footer}
                            {!cancel ? null : (
                                <DrawerClose asChild>
                                    {renderCancelBtn(cancel)}
                                </DrawerClose>
                            )}
                        </DrawerFooter>
                    </>
                )}
            </DrawerContent>
        </Drawer>
    );
}
